<script setup lang="ts">
//#region imports
import { cloneDeep } from 'lodash-es';
import { computed, ref } from 'vue';
import { SMSCalculator } from '@fasttrack-solutions/sms-calculator';
import type { EmitsToListeners } from '@/types/core';
import type { CrmAction, CrmActionGroup } from '@/types/crm';
//#endregion

//#region types
interface Props {
  group?: CrmActionGroup | null;
  payload?: number | boolean | null;
}

interface Emits {
  save: [actionGroup: CrmActionGroup | null, action: CrmAction, index: number];
  close: [];
}

export interface AddPushActionParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const isVisible = ref(false);
const form = ref<CrmAction>({
  name: '',
  type: 'push',
  message: '',
} as unknown as CrmAction);
const index = ref(0);
const actionGroup = ref<CrmActionGroup | null>(null);
const isActive = ref(true);
//#endregion

//#region computed
const messageStats = computed(() => {
  const stats = SMSCalculator.getCount(form.value.message || '');
  return `${stats.numberOfSMS} SMS, ${stats.remaining} Symbols Remaining`;
});
//#endregion

//#region methods
function getDefaultForm(): CrmAction {
  return {
    name: '',
    type: 'push',
    message: '',
  } as unknown as CrmAction;
}

function open() {
  const targetGroup = props.group;
  const payload = props.payload;
  const nextIndex = typeof payload === 'number' || typeof payload === 'boolean'
    ? Number(payload)
    : -1;

  actionGroup.value = targetGroup || null;
  form.value = targetGroup && nextIndex >= 0 && targetGroup.actions?.[nextIndex]
    ? cloneDeep(targetGroup.actions[nextIndex])
    : getDefaultForm();
  index.value = nextIndex;
  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}

function handleSave() {
  emit('save', actionGroup.value, form.value, index.value);
  close();
}

function handleAddParam(param: string) {
  form.value.message = `${form.value.message}{${param}}`;
}
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="crm-add-push-action-popup"
    :title="'Push Action'"
    :width="480"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          class="form-input t:mb-16"
          :label="'Action Name'"
          autosize
          size="big"
          :placeholder="'Action Name'"
          type="text"
        />
        <UiTextarea
          v-model="form.message"
          class="t:mb-4"
          label="Text Message"
          :placeholder="'Text Message'"
          :rows="6"
        />
        <div class="t:flex t:justify-between">
          <CrmActionParams @select="handleAddParam" />
          <span class="crm-add-sms-action-popup__statistics">{{ messageStats }}</span>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="check"
          :disabled="!isActive"
          filled
          @click="handleSave()"
        >
          {{ $t('crm.buttons.save_action') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
